
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

interface ActivityDetailsProps {
  difficultyLevel: string;
  setDifficultyLevel: (value: string) => void;
  duration: string;
  setDuration: (value: string) => void;
  equipmentNeeded: string;
  setEquipmentNeeded: (value: string) => void;
  maxGroupSize: string;
  setMaxGroupSize: (value: string) => void;
  minAge: string;
  setMinAge: (value: string) => void;
  suitableForChildren: boolean;
  setSuitableForChildren: (value: boolean) => void;
  weatherDependent: boolean;
  setWeatherDependent: (value: boolean) => void;
  bestTimeToVisit: string;
  setBestTimeToVisit: (value: string) => void;
  isExistingPlace?: boolean;
}

export function ActivityDetails({
  difficultyLevel,
  setDifficultyLevel,
  duration,
  setDuration,
  equipmentNeeded,
  setEquipmentNeeded,
  maxGroupSize,
  setMaxGroupSize,
  minAge,
  setMinAge,
  suitableForChildren,
  setSuitableForChildren,
  weatherDependent,
  setWeatherDependent,
  bestTimeToVisit,
  setBestTimeToVisit,
  isExistingPlace = false
}: ActivityDetailsProps) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Difficulty Level</Label>
        <Select 
          value={difficultyLevel} 
          onValueChange={setDifficultyLevel}
          disabled={isExistingPlace}
        >
          <SelectTrigger className={isExistingPlace ? "bg-gray-50" : ""}>
            <SelectValue placeholder="Select difficulty" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="easy">Easy</SelectItem>
            <SelectItem value="moderate">Moderate</SelectItem>
            <SelectItem value="challenging">Challenging</SelectItem>
            <SelectItem value="expert">Expert</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Duration</Label>
        <Input
          value={duration}
          onChange={(e) => setDuration(e.target.value)}
          placeholder="e.g., 2 hours, Half day"
          className={isExistingPlace ? "bg-gray-50" : ""}
          readOnly={isExistingPlace}
        />
      </div>

      <div className="space-y-2">
        <Label>Equipment Needed</Label>
        <Input
          value={equipmentNeeded}
          onChange={(e) => setEquipmentNeeded(e.target.value)}
          placeholder="Enter equipment separated by commas"
          className={isExistingPlace ? "bg-gray-50" : ""}
          readOnly={isExistingPlace}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Maximum Group Size</Label>
          <Input
            type="number"
            value={maxGroupSize}
            onChange={(e) => setMaxGroupSize(e.target.value)}
            placeholder="Max participants"
            className={isExistingPlace ? "bg-gray-50" : ""}
            readOnly={isExistingPlace}
          />
        </div>

        <div className="space-y-2">
          <Label>Minimum Age</Label>
          <Input
            type="number"
            value={minAge}
            onChange={(e) => setMinAge(e.target.value)}
            placeholder="Minimum age"
            className={isExistingPlace ? "bg-gray-50" : ""}
            readOnly={isExistingPlace}
          />
        </div>
      </div>
      
      <div className="space-y-2">
        <Label>Best Time to Visit</Label>
        <Input
          value={bestTimeToVisit}
          onChange={(e) => setBestTimeToVisit(e.target.value)}
          placeholder="e.g., Morning, Summer months"
          className={isExistingPlace ? "bg-gray-50" : ""}
          readOnly={isExistingPlace}
        />
      </div>

      <div className="flex items-center space-x-2">
        <Switch
          checked={suitableForChildren}
          onCheckedChange={setSuitableForChildren}
          disabled={isExistingPlace}
        />
        <Label className={isExistingPlace ? "text-muted-foreground" : ""}>
          Suitable for Children
        </Label>
      </div>

      <div className="flex items-center space-x-2">
        <Switch
          checked={weatherDependent}
          onCheckedChange={setWeatherDependent}
          disabled={isExistingPlace}
        />
        <Label className={isExistingPlace ? "text-muted-foreground" : ""}>
          Weather Dependent
        </Label>
      </div>
    </div>
  );
}
